import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { updateAddress } from "@/api/customer-api";
import { useToast } from "@/hooks/use-toast";
import { OrderResponse, OrderStatus } from "@/types/order-type";
import { AlertCircle, MapPin } from "lucide-react"; // Helpful icons
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface PublicSetLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  order: OrderResponse;
  currentAddress?: string | null;
  onSuccess?: () => void;
}

export function PublicSetLocationDialog({
  open,
  onOpenChange,
  customerId,
  order,
  currentAddress,
  onSuccess,
}: PublicSetLocationDialogProps) {
  const [address, setAddress] = useState(currentAddress || "");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Logic: Only allow editing if Order is DRAFT or CONFIRMED
  const canEditAddress = order.status === OrderStatus.DRAFT || order.status === OrderStatus.CONFIRMED;

  // Sync internal state if currentAddress prop changes
  useEffect(() => {
    setAddress(currentAddress || "");
  }, [currentAddress, open]);

  const handleSave = async () => {
    if (!address.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid address",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await updateAddress(customerId, order.id, address.trim());
      if (response.success) {
        toast({
          title: "Success",
          description: "Address updated successfully",
        });
        onOpenChange(false);
        onSuccess?.();
      } else {
        throw new Error(response.message || "Failed to update");
      }
    } catch (error: any) {
      toast({
        title: "Update Failed",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <DialogTitle>Delivery Location</DialogTitle>
          </div>

          {!canEditAddress ? (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Address Locked</AlertTitle>
              <AlertDescription>
                This order is currently <strong>{order.status}</strong>. The address cannot be changed once the order has moved past the confirmation stage.
                Please contact store's support/staff phone or chat to the page.
              </AlertDescription>
            </Alert>
          ) : (
            <DialogDescription className="pt-2">
              Please provide your delivery address in the following format:
              <br />
              <span className="text-xs font-mono bg-muted p-1 rounded mt-1 block">
                House No, Street; Sangkat, Khan; Province / City
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="address" className={!canEditAddress ? "text-muted-foreground" : ""}>
              {canEditAddress ? "Enter Address" : "Current Address"}
            </Label>
            <Textarea
              id="address"
              placeholder="House No, Street, Sangkat..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={5}
              disabled={!canEditAddress}
              className={`resize-none ${!canEditAddress ? "bg-muted cursor-not-allowed" : ""}`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {canEditAddress ? "Cancel" : "Close"}
          </Button>

          {canEditAddress && (
            <Button onClick={handleSave} disabled={isSaving || !address.trim()}>
              {isSaving ? "Saving..." : "Save Address"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}